import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { MapContainer, useMap } from 'react-leaflet';
import Card from '@mui/material/Card';
import { LatLngExpression, LatLngTuple, LeafletEvent } from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { RootState } from '@/index';
import { isEqual } from 'underscore';
import { RouteMapContent } from './RouteMapContent';

type Props = {
  spots?: number[],
  customHeight?: number,
  controls?: any,
  routeColors: Record<string, string>;
  onSelect: (routeOrSpots: number | number[]) => void;
  resizeDeps?: any[];
  visibleRouteIds?: string[];
}

export type StatePolyline = {
  routeId: number,
  coordinates: LatLngTuple[],
  color: string,
}

// Child component that invalidates map size when deps change
const MapResizer: React.FC<{ deps: any[]; delay?: number }> = ({ deps, delay = 150 }) => {
  const map = useMap();
  useEffect(() => {
    const id = setTimeout(() => map.invalidateSize(), delay);
    return () => clearTimeout(id);
  }, [map, ...deps]);
  return null;
};

export const RouteMap = ({ controls, routeColors,  resizeDeps = [], visibleRouteIds }: Props) => {
  const {
    companyAddress: company
  } = useSelector((state: RootState) => state.core, isEqual);
  const [zoomLevel, setZoomLevel] = useState(10);
  const setZoom = (map: LeafletEvent) => {
    setZoomLevel(map.target._zoom);
  };

  const mapCenter: LatLngExpression = useMemo(() => {
    if (company?.location?.coordinates) {
      return [company?.location?.coordinates?.[1], company?.location?.coordinates?.[0]];
    }
    return [0, 0];
  }, [company]);

  return (
    <Card style={{
      position: 'relative',
      height: '100%',
      width: '100%',
    }}>
      <MapContainer
        style={{ position: 'absolute', inset: 0 }}
        center={mapCenter}
        zoomControl={false}
        zoom={zoomLevel}>
        <MapResizer deps={resizeDeps} />
        <RouteMapContent controls={controls} routeColors={routeColors} visibleRouteIds={visibleRouteIds}/>
      </MapContainer>
    </Card>
  );
}; 
